import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeftIcon, PaletteIcon, MailIcon, CheckIcon, SendIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationSettings = () => {
  // Theme Settings State
  const [theme, setTheme] = useState('light');
  const [updatingTheme, setUpdatingTheme] = useState(false);

  // Email Settings State
  const [senderEmail, setSenderEmail] = useState('');
  const [originalSenderEmail, setOriginalSenderEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [loading, setLoading] = useState(true);

  // Test Email State
  const [testEmail, setTestEmail] = useState({
    recipient: '',
    subject: 'Advance Cash Test E-postası',
    message: 'Bu bir test e-postasıdır. SendGrid entegrasyonunuz başarıyla çalışıyor!'
  });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Theme options
  const themeOptions = [
    { value: 'light', label: 'Açık Tema', description: 'Parlak ve temiz görünüm' },
    { value: 'dark', label: 'Koyu Tema', description: 'Göz dostu koyu renk paleti' },
    { value: 'system', label: 'Sistem Varsayılanı', description: 'İşletim sistemi ayarını takip eder' }
  ];

  // Fetch current application settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/app-settings`);
      
      if (response.data) {
        setTheme(response.data.theme || 'light');
        setSenderEmail(response.data.sender_email || '');
        setOriginalSenderEmail(response.data.sender_email || '');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Set default values if API fails
      setTheme('light');
      setSenderEmail('info@quattrostand.com');
      setOriginalSenderEmail('info@quattrostand.com');
    } finally {
      setLoading(false);
    }
  };

  // Update theme setting
  const updateTheme = async () => {
    setUpdatingTheme(true);
    try {
      await axios.put(`${API}/app-settings/theme`, {
        theme: theme
      });
      
      // Apply theme to document
      applyTheme(theme);
      
      toast.success('Tema ayarı başarıyla güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tema güncellenirken hata oluştu');
    } finally {
      setUpdatingTheme(false);
    }
  };

  // Update sender email setting
  const updateSenderEmail = async () => {
    if (!senderEmail.trim()) {
      toast.error('Gönderici e-posta adresi boş olamaz');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail)) {
      toast.error('Geçerli bir e-posta adresi giriniz');
      return;
    }

    setUpdatingEmail(true);
    try {
      await axios.put(`${API}/app-settings/sender-email`, {
        sender_email: senderEmail
      });
      
      setOriginalSenderEmail(senderEmail);
      toast.success('Gönderici e-posta adresi başarıyla güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-posta adresi güncellenirken hata oluştu');
    } finally {
      setUpdatingEmail(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!testEmail.recipient.trim()) {
      toast.error('Alıcı e-posta adresi boş olamaz');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail.recipient)) {
      toast.error('Geçerli bir alıcı e-posta adresi giriniz');
      return;
    }

    if (!testEmail.subject.trim()) {
      toast.error('E-posta konusu boş olamaz');
      return;
    }

    if (!testEmail.message.trim()) {
      toast.error('E-posta içeriği boş olamaz');
      return;
    }

    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/test-email`, testEmail);
      toast.success(`Test e-postası ${testEmail.recipient} adresine başarıyla gönderildi`);
      
      // Clear recipient field after successful send
      setTestEmail(prev => ({...prev, recipient: ''}));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test e-postası gönderilirken hata oluştu');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Apply theme to document
  const applyTheme = (selectedTheme) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('dark', 'light');
    
    if (selectedTheme === 'dark') {
      root.classList.add('dark');
      // Store theme preference
      localStorage.setItem('theme', 'dark');
    } else if (selectedTheme === 'light') {
      root.classList.add('light');
      localStorage.setItem('theme', 'light');
    } else if (selectedTheme === 'system') {
      localStorage.setItem('theme', 'system');
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
      } else {
        root.classList.add('light');
      }
      
      // Listen for system theme changes
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (localStorage.getItem('theme') === 'system') {
          root.classList.remove('dark', 'light');
          root.classList.add(e.matches ? 'dark' : 'light');
        }
      };
      
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Apply theme on component mount and theme change
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner mr-3"></div>
          <span>Uygulama ayarları yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-3">
              <PaletteIcon className="w-5 h-5 text-teal-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Uygulama Ayarları</h3>
          </div>
          <p className="text-gray-600">Uygulamanın genel görünümünü ve entegrasyonlarını yönetin.</p>
        </div>

        <div className="space-y-8">
          {/* Theme Selection — Şimdilik gizli. Tema uygulaması stabil değil
              (koyu modda bazı Şadcn card'ları hatalı renk veriyor). Kararlı
              hale gelince false → true yapmak yeterli. */}
          {false && (
          <Card className="p-6">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <PaletteIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Tema Seçimi</h4>
                <p className="text-sm text-gray-600">Uygulamanın görünümünü kişiselleştirin.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="theme-select" className="form-label">Görünüm Modu</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger data-testid="theme-select" className="form-input">
                    <SelectValue placeholder="Tema seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {themeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-500">{option.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={updateTheme}
                  disabled={updatingTheme}
                  className="btn-primary"
                  data-testid="save-theme-btn"
                >
                  {updatingTheme ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <CheckIcon className="w-4 h-4 mr-2" />
                  )}
                  <span>{updatingTheme ? 'Kaydediliyor...' : 'Kaydet'}</span>
                </Button>
              </div>
            </div>
          </Card>
          )}

          {/* Sender Email Settings */}
          <Card className="p-6">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <MailIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">E-posta Gönderici Ayarı</h4>
                <p className="text-sm text-gray-600">
                  Uygulamadan gönderilecek tüm e-postalar için kullanılacak &quot;Gönderici&quot; adresini belirleyin. 
                  Bu adresin SendGrid hesabınızda doğrulanmış olması gerekir.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="sender-email" className="form-label">Gönderici E-posta Adresi</Label>
                <Input
                  id="sender-email"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  className="form-input"
                  placeholder="info@sirketiniz.com"
                  data-testid="sender-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bu adres SendGrid hesabınızda doğrulanmalıdır
                </p>
              </div>

              {/* SendGrid Integration Info */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm text-amber-800 font-medium">SendGrid Entegrasyonu</p>
                    <p className="text-sm text-amber-700 mt-1">
                      E-posta göndermek için SendGrid servisini kullanıyoruz. Girdiğiniz e-posta adresinin 
                      SendGrid hesabınızda &quot;Verified Sender&quot; olarak eklenmiş olması gerekmektedir.
                    </p>
                    <p className="text-sm text-amber-700 mt-2 font-medium">
                      📝 SendGrid API key&apos;i henüz konfigüre edilmemiş. Gerçek e-posta göndermek için 
                      backend/.env dosyasında SENDGRID_API_KEY değerini ayarlayın.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={updateSenderEmail}
                  disabled={updatingEmail || senderEmail === originalSenderEmail}
                  className="btn-primary"
                  data-testid="save-email-btn"
                >
                  {updatingEmail ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <MailIcon className="w-4 h-4 mr-2" />
                  )}
                  <span>{updatingEmail ? 'Kaydediliyor...' : 'E-postayı Kaydet'}</span>
                </Button>
              </div>
            </div>
          </Card>

          {/* Test Email Sending */}
          <Card className="p-6">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <SendIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Test E-postası Gönder</h4>
                <p className="text-sm text-gray-600">
                  SendGrid entegrasyonunuzun çalışıp çalışmadığını test etmek için bir test e-postası gönderin.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="test-recipient" className="form-label">Alıcı E-posta Adresi</Label>
                  <Input
                    id="test-recipient"
                    type="email"
                    value={testEmail.recipient}
                    onChange={(e) => setTestEmail(prev => ({...prev, recipient: e.target.value}))}
                    className="form-input"
                    placeholder="test@example.com"
                    data-testid="test-recipient-input"
                  />
                </div>
                <div>
                  <Label htmlFor="test-subject" className="form-label">E-posta Konusu</Label>
                  <Input
                    id="test-subject"
                    type="text"
                    value={testEmail.subject}
                    onChange={(e) => setTestEmail(prev => ({...prev, subject: e.target.value}))}
                    className="form-input"
                    placeholder="Test e-postası konusu"
                    data-testid="test-subject-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="test-message" className="form-label">E-posta İçeriği</Label>
                <textarea
                  id="test-message"
                  value={testEmail.message}
                  onChange={(e) => setTestEmail(prev => ({...prev, message: e.target.value}))}
                  className="form-input min-h-[100px] resize-none"
                  placeholder="Test e-postası içeriği..."
                  rows={4}
                  data-testid="test-message-input"
                />
              </div>

              {/* Preview of sender info */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                    <MailIcon className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Gönderici Bilgileri</p>
                    <p className="text-sm text-blue-700">
                      <strong>Gönderici:</strong> {senderEmail || 'info@quattrostand.com'}
                    </p>
                    <p className="text-sm text-blue-700">
                      <strong>Alıcı:</strong> {testEmail.recipient || 'Henüz girilmedi'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={sendTestEmail}
                  disabled={sendingTestEmail || !testEmail.recipient || !testEmail.subject || !testEmail.message}
                  className="btn-primary"
                  data-testid="send-test-email-btn"
                >
                  {sendingTestEmail ? (
                    <div className="loading-spinner mr-2"></div>
                  ) : (
                    <SendIcon className="w-4 h-4 mr-2" />
                  )}
                  <span>{sendingTestEmail ? 'Gönderiliyor...' : 'Test E-postası Gönder'}</span>
                </Button>
              </div>
            </div>
          </Card>

          {/* Additional Settings Preview */}
          <Card className="p-6 bg-gray-50 border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Yakında Geliyor</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Dil ayarları ve çoklu dil desteği</li>
              <li>• Bildirim tercihleri</li>
              <li>• Otomatik yedekleme ayarları</li>
              <li>• API anahtarı yönetimi</li>
              <li>• Güvenlik ayarları ve 2FA</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSettings;